// Shared move-status form for the mobile UI. Mirrors the rule logic in
// MoveStageDialog.tsx (desktop) so a stage move on mobile applies exactly the
// same `require_*` / `clear_*` rules the server enforces in
// app/models/asset/moveable.rb#move_to_stage.

import { useEffect, useMemo, useState } from "react"
import { isoDateInDays } from "@/lib/dates"
import { AlertCircle, AlertTriangle, ArrowLeftRight, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { T, contrastInk } from "../theme"
import { MButton, MChip, MStageBadge, SectionLabel, Touchable } from "../primitives"
import { SearchablePicker } from "../SearchablePicker"
import type {
  Customer,
  MobileAsset,
  Product,
  Stage,
  StageWithRules,
  Tag,
} from "../types"

export interface MoveStageFormValues {
  stage_id: string
  product_id: string
  customer_id: string
  batch_number: string
  expiration_date: string
  tag_ids: Array<string | number>
  notes: string
}

const EMPTY_VALUES: MoveStageFormValues = {
  stage_id: "",
  product_id: "",
  customer_id: "",
  batch_number: "",
  expiration_date: "",
  tag_ids: [],
  notes: "",
}

interface MoveStageFormProps {
  /** Asset type drives which rules apply (`require_product` etc are Keg-only). */
  assetType: MobileAsset["assetable_type"]
  /** Stages the user can pick — already filtered server-side via `canMoveTo`. */
  stages: StageWithRules[]
  products: Product[]
  customers: Customer[]
  tags: Tag[]
  /** Pre-fills used by single-asset moves (asset detail). Defaults to empty for bulk. */
  initial?: Partial<MoveStageFormValues>
  /** Current stage shown in the from→to header. Optional in bulk mode. */
  currentStage?: Stage | null
  /** Headline blurb above the form (e.g. "Aplicado a 3 ativos"). */
  intro?: React.ReactNode
  /** Server-validation errors (keyed like the desktop form: stage_id, product_id, …). */
  errors?: Record<string, string>
  busy?: boolean
  submitLabel?: string
  /** Called on every value change. Used by the bulk scan flow to lift state up. */
  onValuesChange?: (values: MoveStageFormValues) => void
  /** Hide the inline submit button (the parent provides its own). */
  hideSubmit?: boolean
  onSubmit?: (values: MoveStageFormValues, target: StageWithRules) => void
}

export function MoveStageForm({
  assetType,
  stages,
  products,
  customers,
  tags,
  initial,
  currentStage,
  intro,
  errors,
  busy,
  submitLabel,
  onValuesChange,
  hideSubmit,
  onSubmit,
}: MoveStageFormProps) {
  const { t } = useTranslation()
  const resolvedSubmitLabel = submitLabel ?? t("mobile.moveStageForm.submit")
  const [values, setValues] = useState<MoveStageFormValues>({
    ...EMPTY_VALUES,
    ...initial,
    tag_ids: initial?.tag_ids ?? [],
  })

  // Reset whenever `initial` actually changes (e.g. a different asset opens).
  useEffect(() => {
    setValues({
      ...EMPTY_VALUES,
      ...initial,
      tag_ids: initial?.tag_ids ?? [],
    })
  }, [JSON.stringify(initial)])

  // Lift state up for parents that need to aggregate multiple forms (bulk scan).
  useEffect(() => {
    onValuesChange?.(values)
  }, [values, onValuesChange])

  const set = <K extends keyof MoveStageFormValues>(k: K, v: MoveStageFormValues[K]) =>
    setValues((prev) => ({ ...prev, [k]: v }))

  const handleProductChange = (v: string) => {
    setValues((prev) => {
      const selected = products.find((p) => String(p.id) === String(v))
      const next = { ...prev, product_id: v }
      if (v && selected?.default_expiration_days) {
        next.expiration_date = isoDateInDays(selected.default_expiration_days)
      }
      return next
    })
  }

  const movableStages = useMemo(() => stages.filter((s) => s.canMoveTo), [stages])
  const targetStage = useMemo(
    () => stages.find((s) => String(s.id) === String(values.stage_id)) || null,
    [stages, values.stage_id],
  )

  const isKeg = assetType === "Keg"
  const rules = targetStage?.rules || []
  const requiresProduct = isKeg && rules.includes("require_product")
  const requiresCustomer = rules.includes("require_customer")
  const requiresBatch = isKeg && rules.includes("require_batch")
  const clearsProduct = isKeg && rules.includes("clear_product")
  const clearsCustomer = rules.includes("clear_customer")
  const clearsBatch = isKeg && rules.includes("clear_batch")
  const clearsTags = rules.includes("clear_tags")
  const clearsLocation = rules.includes("clear_location")

  const errorMessages = errors ? Object.values(errors).filter(Boolean) : []
  const hasErrors = errorMessages.length > 0

  const canSubmit =
    Boolean(targetStage) &&
    !busy &&
    (!requiresProduct || values.product_id) &&
    (!requiresCustomer || values.customer_id) &&
    (!requiresBatch || values.batch_number.trim().length > 0)

  const handleSubmit = () => {
    if (!targetStage) return
    onSubmit?.(
      {
        stage_id: String(targetStage.id),
        product_id: clearsProduct ? "" : values.product_id,
        customer_id: clearsCustomer ? "" : values.customer_id,
        batch_number: clearsBatch ? "" : values.batch_number,
        expiration_date: clearsProduct ? "" : values.expiration_date,
        tag_ids: clearsTags ? [] : values.tag_ids,
        notes: values.notes,
      },
      targetStage,
    )
  }

  const ruleLines = [
    clearsProduct && t("mobile.moveStageForm.ruleClearProduct"),
    clearsCustomer && t("mobile.moveStageForm.ruleClearCustomer"),
    clearsBatch && t("mobile.moveStageForm.ruleClearBatch"),
    clearsTags && t("mobile.moveStageForm.ruleClearTags"),
    clearsLocation && t("mobile.moveStageForm.ruleClearLocation"),
    requiresProduct && t("mobile.moveStageForm.ruleRequireProduct"),
    requiresCustomer && t("mobile.moveStageForm.ruleRequireCustomer"),
    requiresBatch && t("mobile.moveStageForm.ruleRequireBatch"),
  ].filter(Boolean) as string[]

  return (
    <div style={{ padding: "8px 16px 24px" }}>
      {intro && (
        <div
          style={{
            padding: "10px 12px",
            marginBottom: 16,
            background: T.accentSoft,
            border: `1px solid ${T.accentBorder}`,
            borderRadius: T.radius,
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 13,
            color: T.ink,
            lineHeight: 1.4,
          }}
        >
          {intro}
        </div>
      )}

      {currentStage && targetStage && (
        <div
          style={{
            padding: "12px 14px",
            marginBottom: 16,
            background: T.surfaceAlt,
            borderRadius: T.radius,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
          }}
        >
          <MStageBadge stage={currentStage} size="md" />
          <ChevronRight size={14} color={T.inkSoft} />
          <MStageBadge stage={targetStage} size="md" />
        </div>
      )}

      {hasErrors && (
        <div
          style={{
            padding: "10px 12px",
            marginBottom: 16,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}`,
            borderRadius: T.radius,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <AlertCircle size={16} color={T.danger} style={{ marginTop: 1 }} />
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: "none",
              fontSize: 13,
              color: T.danger,
              lineHeight: 1.45,
            }}
          >
            {errorMessages.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <SectionLabel>
        {t("mobile.moveStageForm.destStage")} <Required visible />
      </SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        {movableStages.map((s) => {
          const active = String(values.stage_id) === String(s.id)
          const ink = contrastInk(s.color)
          return (
            <Touchable
              key={s.id}
              onClick={() => set("stage_id", String(s.id))}
              style={{
                padding: 12,
                background: active ? s.color : T.surfaceAlt,
                color: active ? ink : T.ink,
                border: `1.5px solid ${active ? s.color : "transparent"}`,
                borderRadius: T.radius,
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontFamily: T.fontBody,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: 8, height: 8, borderRadius: 999,
                  background: active ? ink : s.color,
                }}
              />
              {s.name}
            </Touchable>
          )
        })}
      </div>

      {targetStage && ruleLines.length > 0 && (
        <div
          style={{
            padding: "10px 12px",
            marginBottom: 16,
            background: T.warningSoft,
            border: `1px solid ${T.warning}`,
            borderRadius: T.radius,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <AlertTriangle size={16} color={T.warning} style={{ marginTop: 2 }} />
          <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.45 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {t("mobile.moveStageForm.rulesTitle")}
            </div>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {ruleLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Product (Keg only, hidden when stage clears it) */}
      {isKeg && !clearsProduct && (
        <FieldSection
          label={t("mobile.moveStageForm.productLabel")}
          required={requiresProduct}
          error={errors?.product_id}
        >
          <SearchablePicker
            value={values.product_id}
            onChange={handleProductChange}
            options={products}
            title={t("mobile.moveStageForm.productLabel")}
            placeholder={t("mobile.moveStageForm.selectPlaceholder")}
            invalid={Boolean(errors?.product_id)}
            allowClear={!requiresProduct}
          />
        </FieldSection>
      )}

      {/* Expiration date (Keg only, cleared with the product) */}
      {isKeg && !clearsProduct && (
        <FieldSection label={t("mobile.moveStageForm.expirationLabel")}>
          <input
            type="date"
            value={values.expiration_date}
            onChange={(e) => set("expiration_date", e.target.value)}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              borderRadius: T.radius,
              border: `1px solid ${T.border}`,
              background: T.surface,
              fontFamily: T.fontMono,
              // 16px keeps iOS from auto-zooming on focus. See SearchBar.
              fontSize: 16,
              color: T.ink,
              outline: "none",
            }}
          />
        </FieldSection>
      )}

      {/* Batch (Keg only, hidden when stage clears it) */}
      {isKeg && !clearsBatch && (
        <FieldSection
          label={t("mobile.moveStageForm.batchLabel")}
          required={requiresBatch}
          error={errors?.batch_number}
        >
          <input
            value={values.batch_number}
            onChange={(e) => set("batch_number", e.target.value)}
            placeholder={t("mobile.moveStageForm.batchPlaceholder")}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              borderRadius: T.radius,
              border: `1px solid ${errors?.batch_number ? T.danger : T.border}`,
              background: T.surface,
              fontFamily: T.fontMono,
              // 16px keeps iOS from auto-zooming on focus. See SearchBar.
              fontSize: 16,
              color: T.ink,
              outline: "none",
            }}
          />
        </FieldSection>
      )}

      {/* Customer (hidden when stage clears it) */}
      {!clearsCustomer && (
        <FieldSection
          label={t("mobile.moveStageForm.customerLabel")}
          required={requiresCustomer}
          error={errors?.customer_id}
        >
          <SearchablePicker
            value={values.customer_id}
            onChange={(v) => set("customer_id", v)}
            options={customers}
            title={t("mobile.moveStageForm.customerLabel")}
            placeholder={t("mobile.moveStageForm.selectPlaceholder")}
            invalid={Boolean(errors?.customer_id)}
            allowClear={!requiresCustomer}
          />
        </FieldSection>
      )}

      {/* Tags (hidden when stage clears them) */}
      {!clearsTags && tags.length > 0 && (
        <FieldSection label={t("mobile.moveStageForm.tagsLabel")}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {tags.map((tag) => {
              const active = values.tag_ids.some((id) => String(id) === String(tag.id))
              return (
                <MChip
                  key={tag.id}
                  active={active}
                  leftDot
                  color={tag.color}
                  onClick={() => {
                    const next = active
                      ? values.tag_ids.filter((id) => String(id) !== String(tag.id))
                      : [...values.tag_ids, tag.id]
                    set("tag_ids", next)
                  }}
                >
                  {tag.name}
                </MChip>
              )
            })}
          </div>
        </FieldSection>
      )}

      {/* Notes */}
      <FieldSection label={t("mobile.moveStageForm.noteOptional")}>
        <textarea
          value={values.notes}
          onChange={(e) => set("notes", e.target.value)}
          rows={2}
          placeholder={t("mobile.moveStageForm.notePlaceholder")}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            borderRadius: T.radius,
            border: `1px solid ${T.border}`,
            background: T.surface,
            fontFamily: T.fontBody,
            // 16px keeps iOS from auto-zooming on focus. See SearchBar.
            fontSize: 16,
            color: T.ink,
            outline: "none",
            resize: "none",
          }}
        />
      </FieldSection>

      {!hideSubmit && (
        <MButton
          variant="accent"
          fullWidth
          size="lg"
          leftIcon={ArrowLeftRight}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {resolvedSubmitLabel}
        </MButton>
      )}
    </div>
  )
}

interface FieldSectionProps {
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}

function FieldSection({ label, required, error, children }: FieldSectionProps) {
  return (
    <div style={{ marginBottom: 16 }}>
      <SectionLabel>
        {label} <Required visible={required} />
      </SectionLabel>
      {children}
      {error && (
        <div
          style={{
            color: T.danger,
            fontSize: 12,
            marginTop: 6,
            fontFamily: T.fontMono,
            letterSpacing: 0.3,
          }}
        >
          {error}
        </div>
      )}
    </div>
  )
}

function Required({ visible }: { visible?: boolean }) {
  if (!visible) return null
  return <span style={{ color: T.accent, fontWeight: 700 }}>*</span>
}

